import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function About() {
  const { t } = useTranslation();

  const aboutLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': 'https://debugin.com/#organization',
    'name': 'Texio Technologies',
    'alternateName': 'Texio ERP',
    'url': 'https://debugin.com',
    'logo': 'https://debugin.com/vite.svg',
    'description': 'Complete ERP platform built exclusively for textile factories, garment manufacturers, and dyeing houses. Founded in 2018 by textile engineers and software architects.',
    'foundingDate': '2018',
    'founder': {
      '@type': 'Person',
      'name': 'Texio Founding Team',
    },
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'Building 12, Dubai Internet City',
      'addressLocality': 'Dubai',
      'addressCountry': 'AE',
    },
    'contactPoint': {
      '@type': 'ContactPoint',
      'contactType': 'sales',
      'email': 'sales@debugin.com',
      'telephone': '+1-555-123-4567',
      'availableLanguage': ['English', 'Arabic'],
    },
    'sameAs': [
      'https://linkedin.com/company/debugin',
      'https://twitter.com/debugin_erp',
      'https://youtube.com/@debugin_erp',
    ],
  };

  const stats = [
    { number: '150+', label: 'Textile Factories' },
    { number: '12', label: 'Countries' },
    { number: '50K+', label: 'Production Orders/Month' },
    { number: '99.7%', label: 'Uptime SLA' },
  ];

  const values = [
    {
      icon: 'ri-focus-3-line',
      title: 'Industry-First',
      desc: 'Built exclusively for textile manufacturing — we speak the language of GSM, dye lots, shrinkage, and production planning.',
    },
    {
      icon: 'ri-rocket-2-line',
      title: 'Innovation-Driven',
      desc: 'Continuous AI research and development to bring cutting-edge automation to factory floors across the world.',
    },
    {
      icon: 'ri-shield-user-line',
      title: 'Customer-Obsessed',
      desc: 'Every feature is built from real factory floor feedback. Your success metrics are our KPIs.',
    },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <title>About Texio - Complete Textile ERP | Our Story & Mission</title>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutLd) }}
      />

      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground-950 mb-4">
                About Texio
              </h1>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                We are on a mission to digitize the global textile manufacturing industry — one factory at a time.
              </p>
            </div>
          </div>
        </section>

        <section className="pb-12 md:pb-20">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-4xl font-extrabold text-primary-500 mb-1">
                    {stat.number}
                  </p>
                  <p className="text-xs md:text-sm text-foreground-600">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                Texio was founded in 2018 by a team of textile engineers and software architects who saw firsthand how fragmented systems were holding back factory productivity. After spending years on factory floors across Egypt, Turkey, India, and Bangladesh, we built Texio — the first ERP that truly understands textile manufacturing.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-5">
                Unlike generic ERPs that require months of painful customization, Texio comes pre-configured for textile workflows: fabric inspection with 4-point system, dye lot tracking, shrinkage calculation, GSM-based inventory, and production planning with machine-level scheduling.
              </p>
              <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                Today, Texio powers over 150 factories across 12 countries, processing more than 50,000 production orders every month. We are backed by leading industry investors and continue to push the boundaries of textile manufacturing technology — building the most complete ERP platform purpose-built for this industry.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight text-foreground-950 text-center mb-10 md:mb-14">
                Our Values
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                {values.map((v) => (
                  <div key={v.title} className="bg-white rounded-xl p-6 md:p-8 border border-background-200/60 text-center">
                    <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-4">
                      <i className={`${v.icon} text-primary-600 text-xl`}></i>
                    </div>
                    <h3 className="text-base md:text-lg font-bold text-foreground-950 mb-2">{v.title}</h3>
                    <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-20">
          <div className="w-full px-4 md:px-6 lg:px-10 text-center">
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground-950 mb-3">
              Ready to transform your factory?
            </h2>
            <p className="text-sm text-foreground-600 mb-6 max-w-md mx-auto">
              Join 150+ textile manufacturers already running on Texio.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm md:text-base font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              Book a Demo
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}